import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, qs } from '../lib/api';
import { useApp } from '../lib/app-context';
import { GENDER_LABEL, day, mad } from '../lib/format';
import { cityOptions, useCities } from '../lib/places';
import { Avatar, Badge, EmptyState, Field, Select, Sheet, Spinner, TextInput } from '../components/ui';
import type { ColocationRequest } from '../lib/types';
import { t } from '../lib/i18n';

/**
 * The colocation board — the other half of the marketplace, where a student
 * advertises themselves instead of a room. Contact goes through Bedly messaging,
 * which is also why no phone number appears anywhere on this page.
 */
export default function ColocationFeed() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, toast, reportError } = useApp();
  const [items, setItems] = useState<ColocationRequest[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [contacting, setContacting] = useState<ColocationRequest | null>(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const cities = useCities();

  const city = params.get('city') ?? '';
  const maxBudget = params.get('maxBudgetCentimes') ?? '';
  const gender = params.get('genderPreference') ?? '';

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    let active = true;
    setItems(null);
    void (async () => {
      try {
        const response = await api.get<{ items: ColocationRequest[]; nextCursor: string | null }>(
          `/colocation-requests${qs({
            city,
            maxBudgetCentimes: maxBudget,
            genderPreference: gender,
            limit: 24,
          })}`,
        );
        if (!active) return;
        setItems(response.items);
        setNextCursor(response.nextCursor);
      } catch {
        if (active) setItems([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [city, maxBudget, gender]);

  const loadMore = async () => {
    if (!nextCursor || !items) return;
    const response = await api.get<{ items: ColocationRequest[]; nextCursor: string | null }>(
      `/colocation-requests${qs({
        city,
        maxBudgetCentimes: maxBudget,
        genderPreference: gender,
        limit: 24,
        cursor: nextCursor,
      })}`,
    );
    setItems([...items, ...response.items]);
    setNextCursor(response.nextCursor);
  };

  const send = async () => {
    if (!contacting) return;
    setSending(true);
    try {
      const response = await api.post<{ conversationId: string }>(
        `/colocation-requests/${contacting.id}/contact`,
        { body: message },
      );
      setContacting(null);
      setMessage('');
      toast(t("Message envoyé."), 'ok');
      navigate(`/messages/${response.conversationId}`);
    } catch (error) {
      reportError(error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="page page--mid stack stack--loose">
      <div className="row row--between row--wrap" style={{ gap: 12 }}>
        <div className="stack stack--tight">
          <h1 style={{ fontSize: 26 }}>{t("Demandes de colocation")}</h1>
          <p className="muted small" style={{ margin: 0 }}>
            {t("Étudiants et jeunes actifs qui cherchent des colocataires.")}
          </p>
        </div>
        {user ? (
          <Link className="btn" to="/colocations/publier">
            {t("+ Publier ma demande")}
          </Link>
        ) : (
          <Link className="btn" to="/inscription">
            {t("Créer un compte pour publier")}
          </Link>
        )}
      </div>

      <div className="card card--pad row row--wrap" style={{ gap: 10 }}>
        <div style={{ flex: '1 1 150px' }}>
          <Field label={t("Ville")}>
            <Select
              value={city}
              onChange={(value) => setFilter('city', value)}
              options={cityOptions(cities, t('Toutes les villes'))}
            />
          </Field>
        </div>
        <div style={{ flex: '1 1 150px' }}>
          <Field label={t("Budget max (DH/mois)")}>
            <TextInput
              value={maxBudget ? String(Number(maxBudget) / 100) : ''}
              onChange={(value) => {
                const digits = value.replace(/\D/g, '');
                setFilter('maxBudgetCentimes', digits ? String(Number(digits) * 100) : '');
              }}
              inputMode="numeric"
              placeholder="2 000"
            />
          </Field>
        </div>
        <div style={{ flex: '1 1 150px' }}>
          <Field label={t("Préférence")}>
            <Select
              value={gender}
              onChange={(value) => setFilter('genderPreference', value)}
              options={[
                { value: '', label: t("Peu importe") },
                ...Object.entries(GENDER_LABEL).map(([value, label]) => ({ value, label })),
              ]}
            />
          </Field>
        </div>
      </div>

      {items === null ? (
        <Spinner label={t("Chargement des demandes…")} />
      ) : items.length === 0 ? (
        <EmptyState
          title={t("Aucune demande trouvée")}
          description={t("Élargissez le budget ou changez de ville.")}
          action={
            <button className="btn btn--ghost" onClick={() => setParams(new URLSearchParams())}>
              {t("Réinitialiser")}
            </button>
          }
        />
      ) : (
        <>
          <div className="stack">
            {items.map((request) => (
              <article key={request.id} className="card card--pad stack">
                <div className="row row--between row--wrap" style={{ gap: 12 }}>
                  <div className="row" style={{ gap: 12 }}>
                    <Avatar
                      name={request.author?.fullName ?? '?'}
                      url={request.author?.avatarUrl}
                      size={44}
                    />
                    <div className="stack stack--tight">
                      <div className="row row--wrap" style={{ gap: 8 }}>
                        <span className="strong">{request.author?.fullName ?? t("Utilisateur")}</span>
                        {request.author?.identityVerified ? (
                          <Badge tone="badge--verified">{t("Vérifié")}</Badge>
                        ) : null}
                        {request.genderPreference !== 'ANY' ? (
                          <Badge tone="badge--warn">{GENDER_LABEL[request.genderPreference]}</Badge>
                        ) : null}
                        {request.smokingPreference === 'NON_SMOKER' ? <Badge>{t("Non-fumeur")}</Badge> : null}
                      </div>
                      <span className="small muted">
                        {t(request.city)}
                        {request.neighborhood ? ` · ${request.neighborhood}` : ''}
                        {request.universityName ? ` · ${request.universityName}` : ''}
                      </span>
                    </div>
                  </div>
                  <div className="stack stack--tight" style={{ alignItems: 'flex-end' }}>
                    <span className="strong">
                      {mad(request.budget.minCentimes)} – {mad(request.budget.maxCentimes)}
                    </span>
                    <span className="tiny muted">{t("par mois et par personne")}</span>
                  </div>
                </div>

                {request.bio ? (
                  <p className="small" style={{ margin: 0 }}>
                    {request.bio}
                  </p>
                ) : null}

                <div className="row row--between row--wrap" style={{ gap: 10 }}>
                  <span className="small muted">
                    {t("Emménagement")} {day(request.moveInDate, true)} · {request.durationMonths} {t("mois ·")}{' '}
                    {request.flatmatesWanted} {t("coloc recherché(s)")}
                  </span>
                  {user && user.id !== request.author?.id ? (
                    <button
                      className="btn btn--ghost btn--sm"
                      onClick={() => {
                        setContacting(request);
                        setMessage('');
                      }}
                    >
                      {t("Message")}
                    </button>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
          {nextCursor ? (
            <div className="center">
              <button className="btn btn--ghost" onClick={loadMore}>
                {t("Afficher plus")}
              </button>
            </div>
          ) : null}
        </>
      )}

      {contacting ? (
        <Sheet title={contacting.author?.fullName ? t('Répondre à {name}', { name: contacting.author.fullName }) : t('Répondre à cette demande')} onClose={() => setContacting(null)}>
          <div className="stack">
            <p className="small muted" style={{ margin: 0 }}>
              {t(contacting.city)} · {mad(contacting.budget.minCentimes)} –{' '}
              {mad(contacting.budget.maxCentimes)}/mois
            </p>
            <textarea
              className="textarea"
              rows={5}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder={t("Présentez votre logement ou votre recherche…")}
            />
            <button
              className="btn btn--block"
              disabled={sending || message.trim().length === 0}
              onClick={send}
            >
              {sending ? t("Envoi…") : t("Envoyer")}
            </button>
          </div>
        </Sheet>
      ) : null}
    </div>
  );
}
